import { z } from 'zod';

const dateSchema = z.union([z.date(), z.string().datetime()]);

export const roleSchema = z.enum(['owner', 'admin', 'manager', 'coordinator', 'staff']);

export const userSchema = z.object({
  uid: z.string().min(1),
  email: z.string().email(),
  displayName: z.string().optional(),
  photoURL: z.string().url().optional(),
  createdAt: dateSchema,
  updatedAt: dateSchema,
});

export const orgMemberSchema = z.object({
  uid: z.string().min(1),
  role: roleSchema,
  joinedAt: dateSchema,
});

export const inviteSchema = z.object({
  email: z.string().email(),
  role: roleSchema,
  invitedBy: z.string().min(1),
  createdAt: dateSchema,
});

export const holidaySchema = z.object({
  date: dateSchema,
  name: z.string().min(1),
});

export const organizationSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  ownerId: z.string().min(1),
  members: z.record(orgMemberSchema),
  invoiceNinjaBaseUrl: z.string().url().optional(),
  invoiceNinjaConnected: z.boolean().optional(),
  invoiceNinjaCompanyName: z.string().optional(),
  invoiceNinjaConnectedAt: dateSchema.optional(),
  defaultLaborMarginPct: z.number().nonnegative().optional(),
  replyToEmail: z.string().email().optional(),
  timezone: z.string().optional(),
  holidays: z.array(holidaySchema).optional(),
  contractTermsHtml: z.string().optional(),
  logoPath: z.string().optional(),
  logoContentType: z.string().optional(),
  createdAt: dateSchema,
  updatedAt: dateSchema,
});

export const addressSchema = z.object({
  street: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  postalCode: z.string().optional(),
});

export const clientSchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  name: z.string().min(1),
  contactEmail: z.string().email().optional(),
  contactPhone: z.string().optional(),
  address: addressSchema.optional(),
  invoiceNinjaClientId: z.string().optional(),
  syncedAt: dateSchema.optional(),
  invoiceNinjaPushedAt: dateSchema.optional(),
  createdAt: dateSchema,
  updatedAt: dateSchema,
});

export const venueContactSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  role: z.string().optional(),
  email: z.string().email().optional(),
  phone: z.string().optional(),
});

export const venueRoomSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  notes: z.string().optional(),
});

export const venueSchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  name: z.string().min(1),
  address: addressSchema.optional(),
  contacts: z.array(venueContactSchema),
  rooms: z.array(venueRoomSchema),
  timeZone: z.string().optional(),
  notes: z.string().optional(),
  createdAt: dateSchema,
  updatedAt: dateSchema,
});

export const personSchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  name: z.string().min(1),
  email: z.string().email().optional(),
  phone: z.string().optional(),
  address: addressSchema.optional(),
  roles: z.array(z.string()),
  notes: z.string().optional(),
  userId: z.string().optional(),
  createdAt: dateSchema,
  updatedAt: dateSchema,
});

export const rateEntrySchema = z.object({
  amount: z.number().nonnegative(),
  currency: z.string().length(3),
});

export const rateCardRoleSchema = z.object({
  dayRate: rateEntrySchema,
  hourlyRate: rateEntrySchema,
  payDayRate: rateEntrySchema.optional(),
  payHourlyRate: rateEntrySchema.optional(),
  minHourlyBill: z.number().nonnegative().optional(),
  restPeriodHrs: z.number().positive().optional(),
  otStartHrs: z.number().positive().optional(),
  useStandardTimekeeping: z.boolean().optional(),
  otSecondStartHrs: z.number().positive().optional(),
  otSecondMultiplier: z.number().positive().optional(),
  firstCallMinHrs: z.number().nonnegative().optional(),
  subsequentCallMinHrs: z.number().nonnegative().optional(),
});

export const rateCardSchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  name: z.string().min(1),
  roles: z.record(rateCardRoleSchema),
  createdAt: dateSchema,
  updatedAt: dateSchema,
});

export const itemSchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  name: z.string().min(1),
  description: z.string().optional(),
  productKey: z.string().optional(),
  unitCost: z.number().nonnegative(),
  taxName: z.string().optional(),
  taxRate: z.number().nonnegative().optional(),
  invoiceNinjaProductId: z.string().optional(),
  syncedAt: dateSchema.optional(),
  createdAt: dateSchema,
  updatedAt: dateSchema,
});

export const quoteLineSourceSchema = z.enum(['labor', 'item', 'manual']);

export const quoteLineItemSchema = z.object({
  id: z.string().min(1),
  source: quoteLineSourceSchema,
  description: z.string().min(1),
  quantity: z.number().positive(),
  unitCost: z.number().nonnegative(),
  lineTotal: z.number().nonnegative(),
  itemId: z.string().optional(),
  itemDescription: z.string().optional(),
  productKey: z.string().optional(),
  callId: z.string().optional(),
  callIds: z.array(z.string()).optional(),
  positionId: z.string().optional(),
  rateCardId: z.string().optional(),
  hours: z.number().nonnegative().optional(),
  tier: z.union([z.literal(0), z.literal(1)]).optional(),
  locked: z.boolean().optional(),
  overageDecisionId: z.string().optional(),
});

export const quoteStateSchema = z.enum(['DRAFT', 'SENT', 'APPROVED', 'DECLINED']);

export const quoteSchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  gigId: z.string().min(1),
  clientId: z.string().min(1),
  state: quoteStateSchema,
  currency: z.string().length(3),
  lineItems: z.array(quoteLineItemSchema),
  notes: z.string().optional(),
  taxRate: z.number().nonnegative().optional(),
  depositAmount: z.number().nonnegative().optional(),
  subtotal: z.number().nonnegative(),
  tax: z.number().nonnegative(),
  total: z.number().nonnegative(),
  sentAt: dateSchema.optional(),
  approvedAt: dateSchema.optional(),
  approvedBy: z.string().optional(),
  invoiceNinjaInvoiceId: z.string().optional(),
  invoicedAt: dateSchema.optional(),
  parentQuoteId: z.string().optional(),
  changeOrderNumber: z.number().int().nonnegative().optional(),
  createdAt: dateSchema,
  updatedAt: dateSchema,
});

export const penaltyTierSchema = z.object({
  tier: z.enum(['0', '1'] as const).transform(v => parseInt(v) as 0 | 1),
  dayMultiplier: z.number().nonnegative(),
  otMultiplier: z.number().nonnegative(),
});

export const tierSystemSchema = z.object({
  tier0: penaltyTierSchema,
  tier1: penaltyTierSchema,
});

export const gigStateSchema = z.enum(['DRAFT', 'QUOTED', 'BOOKED', 'COMPLETED', 'INVOICED', 'PAID', 'CANCELLED']);

export const gigSchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  clientId: z.string().min(1),
  clientName: z.string().optional(),
  name: z.string().min(1),
  description: z.string().optional(),
  venueId: z.string().optional(),
  state: gigStateSchema,
  date: dateSchema,
  invoiceNinjaInvoiceId: z.string().optional(),
  activeQuoteId: z.string().optional(),
  activeContractId: z.string().optional(),
  calls: z.array(z.string()),
  laborMarginPctOverride: z.number().nonnegative().optional(),
  createdAt: dateSchema,
  updatedAt: dateSchema,
});

export const callTypeSchema = z.enum(['setup', 'show', 'strike']);

export const callSchema = z.object({
  id: z.string().min(1),
  gigId: z.string().min(1),
  orgId: z.string().min(1),
  type: callTypeSchema,
  startTime: dateSchema,
  endTime: dateSchema,
  venueId: z.string().optional(),
  notes: z.string().optional(),
  icsSequence: z.number().int().nonnegative().optional(),
  createdAt: dateSchema,
  updatedAt: dateSchema,
});

export const positionSchema = z.object({
  id: z.string().min(1),
  gigId: z.string().min(1),
  orgId: z.string().min(1),
  role: z.string().min(1),
  label: z.string().optional(),
  quantity: z.number().positive().int(),
  rate: rateEntrySchema,
  rateType: z.enum(['day', 'hourly']).optional(),
  maxBudget: z.number().nonnegative().optional(),
  rateCardId: z.string().optional(),
  rateCardRole: z.string().optional(),
  callIds: z.array(z.string()),
  venueId: z.string().optional(),
  scopeDate: dateSchema.optional(),
  bookings: z.array(z.string()),
  state: z.enum(['OPEN', 'FILLED', 'BOOKED']),
  createdAt: dateSchema,
  updatedAt: dateSchema,
});

export const bookingStateSchema = z.enum(['DRAFT', 'OFFERED', 'ACCEPTED', 'DECLINED', 'CANCELLED', 'BOOKED', 'COMPLETED', 'INVOICED']);

export const cancellationFeeStatusSchema = z.enum(['OWED', 'WAIVED', 'PAID']);

export const linkedCallSchema = z.object({
  callId: z.string().min(1),
  type: callTypeSchema,
  startTime: dateSchema,
  endTime: dateSchema,
  venueId: z.string().optional(),
  venueName: z.string().optional(),
  timeZone: z.string().optional(),
  icsSequence: z.number().nonnegative().optional(),
});

export const bookingOfferTermsSchema = z.object({
  otStartHrs: z.number(),
  restPeriodHrs: z.number().optional(),
  minHourlyBill: z.number().optional(),
  useStandardTimekeeping: z.boolean().optional(),
  otSecondStartHrs: z.number().optional(),
  otSecondMultiplier: z.number().optional(),
  firstCallMinHrs: z.number().optional(),
  subsequentCallMinHrs: z.number().optional(),
});

export const bookingSchema = z.object({
  id: z.string().min(1),
  personId: z.string().min(1),
  positionId: z.string().min(1),
  gigId: z.string().min(1),
  orgId: z.string().min(1),
  state: bookingStateSchema,
  tier: z.enum(['0', '1'] as const).transform(v => parseInt(v) as 0 | 1),
  offeredAt: dateSchema,
  acceptedAt: dateSchema.optional(),
  callIds: z.array(z.string()),
  cancelledAt: dateSchema.optional(),
  cancelledBy: z.string().optional(),
  cancellationReason: z.string().optional(),
  cancellationFee: rateEntrySchema.optional(),
  cancellationFeeStatus: cancellationFeeStatusSchema.optional(),
  dayHoldIcsSequence: z.number().nonnegative().optional(),
  notes: z.string().optional(),
  createdAt: dateSchema,
  updatedAt: dateSchema,
  userId: z.string().optional(),
  gigName: z.string().optional(),
  role: z.string().optional(),
  rate: rateEntrySchema.optional(),
  rateType: z.enum(['day', 'hourly']).optional(),
  personName: z.string().optional(),
  venueId: z.string().optional(),
  venueName: z.string().optional(),
  venueTimeZone: z.string().optional(),
  scopeDate: dateSchema.optional(),
  linkedCalls: z.array(linkedCallSchema).optional(),
  offerTerms: bookingOfferTermsSchema.optional(),
});

export const timesheetEntryStatusSchema = z.enum(['OPEN', 'PENDING', 'APPROVED', 'REJECTED']);

export const timesheetEntrySchema = z.object({
  id: z.string().min(1),
  personId: z.string().min(1),
  userId: z.string().optional(),
  personName: z.string().optional(),
  bookingId: z.string().min(1),
  positionId: z.string().min(1),
  callId: z.string().optional(),
  gigId: z.string().min(1),
  orgId: z.string().min(1),
  status: timesheetEntryStatusSchema,
  source: z.enum(['clock', 'manual']),
  clockIn: dateSchema,
  clockOut: dateSchema.optional(),
  breakMinutes: z.number().int().nonnegative().optional(),
  notes: z.string().optional(),
  submittedAt: dateSchema.optional(),
  reviewedAt: dateSchema.optional(),
  reviewedBy: z.string().optional(),
  reviewNote: z.string().optional(),
  originalClockIn: dateSchema.optional(),
  originalClockOut: dateSchema.optional(),
  createdBy: z.string().min(1),
  createdAt: dateSchema,
  updatedAt: dateSchema,
});

export const contractStateSchema = z.enum(['DRAFT', 'SENT', 'SIGNED', 'ARCHIVED']);

export const contractSchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  gigId: z.string().min(1),
  quoteId: z.string().min(1),
  clientName: z.string().min(1),
  clientEmail: z.string().email(),
  state: contractStateSchema,
  lineItems: z.array(quoteLineItemSchema),
  subtotal: z.number().nonnegative(),
  taxRate: z.number().nonnegative().optional(),
  total: z.number().nonnegative(),
  termsHtml: z.string().optional(),
  sentAt: dateSchema.optional(),
  signedAt: dateSchema.optional(),
  signatureName: z.string().optional(),
  signatureTimestamp: dateSchema.optional(),
  pdfUrl: z.string().optional(), // Cloud Storage path, not a URL — see Contract.pdfUrl
  createdAt: dateSchema,
  updatedAt: dateSchema,
});

export const notificationEventTypeSchema = z.enum([
  'offer.created',
  'offer.accepted',
  'offer.declined',
  'offer.reminder',
  'gig.booked',
  'gig.cancelled',
  'booking.cancelled',
  'booking.callsChanged',
  'call.timeChanged',
  'call.locationChanged',
  'contract.sent',
  'contract.signed',
  'callTime.reminder',
]);

export const notificationSchema = z.object({
  id: z.string().min(1),
  recipientId: z.string().min(1),
  orgId: z.string().min(1).optional(),
  eventType: notificationEventTypeSchema,
  relatedGigId: z.string().optional(),
  relatedBookingId: z.string().optional(),
  title: z.string().min(1),
  body: z.string().min(1),
  read: z.boolean(),
  readAt: dateSchema.optional(),
  createdAt: dateSchema,
});

export const payrollDaySchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  billedHours: z.number().nonnegative(),
  regularHours: z.number().nonnegative(),
  ot1Hours: z.number().nonnegative(),
  ot2Hours: z.number().nonnegative(),
  holiday: z.boolean(),
  shortRest: z.boolean(),
  cost: z.number().nonnegative(),
});

export const payrollPaidMarkerSchema = z.object({
  paymentId: z.string().optional(),
  paidAt: dateSchema,
  reference: z.string().optional(),
});

export const payrollEntrySchema = z.object({
  personId: z.string().min(1),
  bookingId: z.string().min(1),
  positionId: z.string().min(1),
  name: z.string().min(1),
  role: z.string().min(1),
  label: z.string().optional(),
  rateType: z.enum(['day', 'hourly']),
  rate: z.number().nonnegative(),
  workedHours: z.number().nonnegative(),
  days: z.array(payrollDaySchema),
  grossPay: z.number().nonnegative(),
  paid: payrollPaidMarkerSchema.optional(),
  sharedDayId: z.string().optional(),
  sharedWith: z.array(z.string()).optional(),
});

export const payrollFeeSchema = z.object({
  bookingId: z.string().min(1),
  personId: z.string().min(1),
  name: z.string().min(1),
  role: z.string().min(1),
  amount: z.number().nonnegative(),
  reason: z.string().optional(),
  paid: payrollPaidMarkerSchema.optional(),
});

export const payrollPaymentLineSchema = z.object({
  gigId: z.string().min(1),
  gigName: z.string().min(1),
  bookingId: z.string().min(1),
  kind: z.enum(['work', 'fee']),
  role: z.string().min(1),
  label: z.string().optional(),
  rateType: z.enum(['day', 'hourly']).optional(),
  rate: z.number().nonnegative().optional(),
  workedHours: z.number().nonnegative().optional(),
  days: z.array(payrollDaySchema).optional(),
  amount: z.number().nonnegative(),
  reason: z.string().optional(),
  sharedWith: z.array(z.string()).optional(),
});

export const payrollPaymentSchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  personId: z.string().min(1),
  name: z.string().min(1),
  lines: z.array(payrollPaymentLineSchema).min(1),
  total: z.number().positive(),
  paidAt: dateSchema,
  reference: z.string().optional(),
  pdfPath: z.string().optional(),
  createdBy: z.string().min(1),
  createdAt: dateSchema,
});

export const payrollReportStateSchema = z.enum(['DRAFT', 'PROCESSED', 'PAID']);

export const payrollReportSchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  gigId: z.string().min(1),
  gigName: z.string().min(1),
  state: payrollReportStateSchema,
  version: z.number().int().positive(),
  entries: z.array(payrollEntrySchema),
  fees: z.array(payrollFeeSchema),
  totalCost: z.number().nonnegative(),
  warnings: z.array(z.string()),
  sourceEntryIds: z.array(z.string()),
  generatedBy: z.string().min(1),
  generatedAt: dateSchema,
  processedBy: z.string().optional(),
  processedAt: dateSchema.optional(),
  paidBy: z.string().optional(),
  paidAt: dateSchema.optional(),
  paymentReference: z.string().optional(),
  reopenReason: z.string().optional(),
  pdfPath: z.string().optional(),
});

export const overageBreakdownRowSchema = z.object({
  positionId: z.string().min(1),
  role: z.string().min(1),
  label: z.string().optional(),
  budget: z.number().nonnegative(),
  actual: z.number().nonnegative(),
  difference: z.number(),
});

export const overageDecisionSchema = z
  .object({
    id: z.string().min(1),
    orgId: z.string().min(1),
    gigId: z.string().min(1),
    decision: z.enum(['BILL', 'ABSORB']),
    baselineCost: z.number().nonnegative(),
    actualCost: z.number().nonnegative(),
    overageCost: z.number().positive(),
    marginPct: z.number().nonnegative(),
    billAmount: z.number().nonnegative(),
    breakdown: z.array(overageBreakdownRowSchema),
    notes: z.string().optional(),
    billedQuoteId: z.string().optional(),
    billedLineId: z.string().optional(),
    supersedes: z.string().optional(),
    decidedBy: z.string().min(1),
    decidedAt: dateSchema,
  })
  .refine(d => d.decision !== 'ABSORB' || !!d.notes?.trim(), {
    message: 'A reason is required to absorb an overage',
    path: ['notes'],
  })
  .refine(d => d.decision !== 'BILL' || d.billAmount > 0, {
    message: 'A billed overage needs an amount',
    path: ['billAmount'],
  });

export const sharedDayMemberSchema = z.object({
  gigId: z.string().min(1),
  positionId: z.string().min(1),
  bookingId: z.string().min(1),
});

export const sharedDaySchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  personId: z.string().min(1),
  personName: z.string().optional(),
  members: z.array(sharedDayMemberSchema).min(2),
  primaryBookingId: z.string().min(1),
  rate: z.object({ amount: z.number().positive(), currency: z.string().min(1) }),
  rateType: z.enum(['day', 'hourly']),
  createdBy: z.string().min(1),
  createdAt: dateSchema,
});
